import classNames from 'classnames';
import { ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { marketing } from 'config/global';

const CONCEPT_KEYS = ['noFeeds', 'noSystem', 'yours'];

interface Tile {
  key: string;
  accent: string;
  icon: ReactNode;
}

const TILES: Tile[] = [
  {
    key: 'photos',
    accent: '#7fa8ff',
    icon: (
      <>
        <rect x="3.5" y="4.5" width="17" height="15" rx="2" />
        <circle cx="8.5" cy="9" r="1.5" />
        <path d="m4 18 5-5 4 4 3-3 4 4" />
      </>
    ),
  },
  {
    key: 'stories',
    accent: '#b69bff',
    icon: <path d="M6 5h12M6 9h12M6 13h8M6 17h10" />,
  },
  {
    key: 'links',
    accent: '#6ed7a6',
    icon: (
      <>
        <path d="M9 14a4 4 0 0 1 0-6l2-2a4 4 0 1 1 6 6l-1 1" />
        <path d="M15 10a4 4 0 0 1 0 6l-2 2a4 4 0 1 1-6-6l1-1" />
      </>
    ),
  },
  {
    key: 'moments',
    accent: '#FDE47A',
    icon: (
      <path d="M12 20s-7-4.5-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.5-7 10-7 10Z" />
    ),
  },
];

const NUMS: Record<string, string> = {
  noFeeds: '01',
  noSystem: '02',
  yours: '03',
};

const DeviceIcon = () => (
  <svg viewBox="0 0 24 24" className={styles.storeGlyph} aria-hidden="true">
    <rect x="3" y="4" width="13" height="10" rx="1.5" />
    <rect x="16" y="7" width="5" height="13" rx="1.5" />
    <path d="M3 14h13M7 18h5" />
  </svg>
);

export const Section28 = () => {
  const t = useTranslations();
  return (
    <section className={styles.section}>
      <div className={styles.wrap}>
        {/* The answer */}
        <div className={styles.head}>
          <div className={styles.eyebrow}>
            {t('pages.useCases.memoryKeeping.answer.eyebrow')}
          </div>
          <h2 className={styles.bigTitle}>
            {t.rich('pages.useCases.memoryKeeping.answer.title', {
              highlight: (chunks) => (
                <span className={styles.highlight}>{chunks}</span>
              ),
              br: () => <br />,
            })}
          </h2>
          <p className={styles.lead}>
            {t('pages.useCases.memoryKeeping.answer.lead')}
          </p>
        </div>

        <div className={styles.concepts}>
          {CONCEPT_KEYS.map((key) => (
            <div key={key} className={styles.concept}>
              <div className={styles.conceptNum}>{NUMS[key]}</div>
              <h3 className={styles.conceptTitle}>
                {t(`pages.useCases.memoryKeeping.answer.concepts.${key}.title`)}
              </h3>
              <p className={styles.conceptBody}>
                {t(`pages.useCases.memoryKeeping.answer.concepts.${key}.body`)}
              </p>
            </div>
          ))}
        </div>

        <div className={styles.stores}>
          <a
            href={marketing.appStoreUri}
            target="_blank"
            rel="noreferrer noopener"
            className={styles.storePrimary}
          >
            <DeviceIcon />
            {t('pages.useCases.memoryKeeping.storeIos')}
          </a>
          <a
            href={marketing.googlePlayUri}
            target="_blank"
            rel="noreferrer noopener"
            className={styles.storeOutline}
          >
            <DeviceIcon />
            {t('pages.useCases.memoryKeeping.storeAndroid')}
          </a>
        </div>

        <div className={styles.divider} />

        {/* What you can keep */}
        <div className={styles.head}>
          <div className={styles.eyebrow}>
            {t('pages.useCases.memoryKeeping.keep.eyebrow')}
          </div>
          <h2 className={styles.title}>
            {t('pages.useCases.memoryKeeping.keep.title')}
          </h2>
          <p className={styles.lead}>
            {t('pages.useCases.memoryKeeping.keep.lead')}
          </p>
        </div>

        <div className={styles.tiles}>
          {TILES.map((tile) => (
            <div key={tile.key} className={styles.tile}>
              <span
                className={styles.tileIcon}
                style={{ background: `${tile.accent}22`, color: tile.accent }}
              >
                <svg viewBox="0 0 24 24" className={styles.glyph}>
                  {tile.icon}
                </svg>
              </span>
              <h3 className={styles.tileTitle}>
                {t(`pages.useCases.memoryKeeping.keep.tiles.${tile.key}.title`)}
              </h3>
              <p className={styles.tileBody}>
                {t(`pages.useCases.memoryKeeping.keep.tiles.${tile.key}.body`)}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const styles = {
  section: 'marketing-dark:bg-mf-navy pt-[74px] pb-5 text-center',
  wrap: 'mx-auto max-w-[1200px] px-6 lg:px-10',
  head: 'mx-auto max-w-[680px]',
  eyebrow:
    'mb-4 text-[13px] font-semibold uppercase tracking-[0.15em] text-mf-blue marketing-dark:text-mf-coral',
  bigTitle:
    'font-inter text-[40px] font-extrabold leading-[1.05] tracking-[-0.02em] text-mf-off-black-600 marketing-dark:text-white sm:text-[58px]',
  highlight: 'text-mf-blue marketing-dark:text-mf-coral',
  title:
    'font-inter text-3xl font-extrabold leading-[1.1] tracking-[-0.02em] text-mf-off-black-600 marketing-dark:text-white sm:text-[40px]',
  lead: 'mx-auto mt-6 max-w-[600px] text-lg leading-[1.6] text-mf-gray-600 marketing-dark:text-white/66',
  concepts: 'mt-14 grid grid-cols-1 gap-[22px] text-left lg:grid-cols-3',
  concept: classNames(
    'rounded-[18px] border px-8 py-8',
    'border-[#F0F0F0] bg-white',
    'marketing-dark:border-white/10 marketing-dark:bg-white/[0.03]',
  ),
  conceptNum:
    'text-sm font-bold tracking-[0.2em] text-mf-blue marketing-dark:text-mf-coral',
  conceptTitle:
    'mt-2 font-inter text-xl font-bold text-mf-off-black-600 marketing-dark:text-white',
  conceptBody:
    'mt-3 text-sm leading-[1.65] text-mf-gray-600 marketing-dark:text-white/60',
  stores: 'mt-10 flex flex-wrap items-center justify-center gap-3',
  storePrimary:
    'inline-flex items-center gap-2 rounded-full bg-mf-blue px-6 py-3 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5',
  storeOutline: classNames(
    'inline-flex items-center gap-2 rounded-full border px-6 py-3 text-sm font-semibold transition-colors',
    'border-mf-gray-300 bg-white text-mf-off-black-600 hover:border-mf-gray-500',
    'marketing-dark:border-white/25 marketing-dark:bg-transparent marketing-dark:text-white marketing-dark:hover:border-white/60',
  ),
  storeGlyph: 'h-[15px] w-[15px] fill-none stroke-current stroke-2',
  divider:
    'mx-auto my-20 max-w-[920px] border-t border-[#F0F0F0] marketing-dark:border-white/10',
  tiles:
    'mx-auto mt-12 grid max-w-[760px] grid-cols-1 gap-5 text-left sm:grid-cols-2',
  tile: classNames(
    'rounded-[18px] border p-7 transition-transform duration-300 hover:-translate-y-1',
    'border-[#F0F0F0] bg-white',
    'marketing-dark:border-white/10 marketing-dark:bg-white/[0.03]',
  ),
  tileIcon:
    'mb-5 flex h-[54px] w-[54px] items-center justify-center rounded-[14px]',
  glyph: 'h-6 w-6 fill-none stroke-current stroke-2',
  tileTitle:
    'mb-2 font-inter text-lg font-bold text-mf-off-black-600 marketing-dark:text-white',
  tileBody:
    'text-sm leading-[1.65] text-mf-gray-600 marketing-dark:text-white/60',
};
